import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  SlotTheme,
  SlotThemeContextValue,
  RenderQuality,
  SymbolId,
  SymbolDefinition,
} from '../themes/types';

// =============================================================================
// Context
// =============================================================================

const SlotThemeContext = createContext<SlotThemeContextValue | null>(null);

// =============================================================================
// GPU Detection Utility
// =============================================================================

interface GPUInfo {
  renderer: string;
  vendor: string;
  score: number;
}

function detectGPUCapability(): GPUInfo {
  const canvas = document.createElement('canvas');
  const gl = (canvas.getContext('webgl2') ||
    canvas.getContext('webgl') ||
    canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

  if (!gl) {
    return { renderer: 'unknown', vendor: 'unknown', score: 0 };
  }

  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = debugInfo
    ? (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)
    : 'unknown';
  const vendor = debugInfo
    ? (gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string)
    : 'unknown';

  // Calculate a rough GPU score based on known GPU patterns
  let score = 50; // Default mid-range

  const rendererLower = renderer.toLowerCase();

  // High-end indicators
  if (
    rendererLower.includes('rtx') ||
    rendererLower.includes('rx 6') ||
    rendererLower.includes('rx 7') ||
    rendererLower.includes('m1') ||
    rendererLower.includes('m2') ||
    rendererLower.includes('m3') ||
    rendererLower.includes('apple gpu') ||
    rendererLower.includes('radeon pro')
  ) {
    score = 90;
  }
  // Mid-range indicators
  else if (
    rendererLower.includes('gtx') ||
    rendererLower.includes('rx 5') ||
    rendererLower.includes('intel iris') ||
    rendererLower.includes('radeon')
  ) {
    score = 60;
  }
  // Low-end indicators
  else if (
    rendererLower.includes('intel hd') ||
    rendererLower.includes('intel uhd') ||
    rendererLower.includes('mali') ||
    rendererLower.includes('adreno') ||
    rendererLower.includes('powervr')
  ) {
    score = 30;
  }
  // Software rendering
  else if (
    rendererLower.includes('swiftshader') ||
    rendererLower.includes('llvmpipe')
  ) {
    score = 10;
  }

  // Mobile detection penalty
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  ) {
    score = Math.min(score, 40); // Cap mobile at 40
  }

  return { renderer, vendor, score };
}

function determineRenderQuality(
  gpuScore: number,
  thresholds: { high: number; medium: number }
): RenderQuality {
  if (gpuScore >= thresholds.high) return 'high';
  if (gpuScore >= thresholds.medium) return 'medium';
  if (gpuScore > 10) return 'low';
  return '2d';
}

// =============================================================================
// Provider Props
// =============================================================================

interface SlotThemeProviderProps {
  /** The theme configuration to use */
  theme: SlotTheme;
  /** Optional: Force a specific render quality (disables adaptive) */
  forceQuality?: RenderQuality;
  /** Optional: Base URL for theme assets */
  assetBaseUrl?: string;
  /** Children components */
  children: ReactNode;
}

// =============================================================================
// Provider Component
// =============================================================================

export function SlotThemeProvider({
  theme,
  forceQuality,
  assetBaseUrl = '',
  children,
}: SlotThemeProviderProps) {
  // Audio state
  const [audioEnabled, setAudioEnabled] = useState(true);

  // Render quality state
  const [renderQuality, setRenderQualityState] = useState<RenderQuality>(() => {
    if (forceQuality) return forceQuality;

    // Check localStorage for user preference
    const stored = localStorage.getItem('slot-machine-quality');
    if (stored && ['high', 'medium', 'low', '2d'].includes(stored)) {
      return stored as RenderQuality;
    }

    // Auto-detect on mount
    return 'medium'; // Default, will be updated in useEffect
  });

  // Auto-detect GPU capability on mount
  useEffect(() => {
    if (forceQuality) return;

    const stored = localStorage.getItem('slot-machine-quality');
    if (stored) return; // User has set a preference

    if (theme.adaptive?.enabled) {
      // Schedule state update to avoid cascading renders
      requestAnimationFrame(() => {
        const gpuInfo = detectGPUCapability();
        const quality = determineRenderQuality(gpuInfo.score, {
          high: theme.adaptive?.highThreshold ?? 0.7,
          medium: theme.adaptive?.mediumThreshold ?? 0.4,
        });
        setRenderQualityState(quality);

        if (process.env.NODE_ENV === 'development') {
          console.log('[SlotMachine] GPU Detection:', gpuInfo);
          console.log('[SlotMachine] Selected quality:', quality);
        }
      });
    }
  }, [forceQuality, theme.adaptive]);

  // Quality setter that persists to localStorage
  const setRenderQuality = useCallback((quality: RenderQuality) => {
    setRenderQualityState(quality);
    localStorage.setItem('slot-machine-quality', quality);
  }, []);

  // Symbol lookup helper
  const getSymbol = useCallback(
    (id: SymbolId): SymbolDefinition | undefined => {
      return theme.symbols[id];
    },
    [theme.symbols]
  );

  // Asset URL helper
  const getAssetUrl = useCallback(
    (path: string): string => {
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      // Remove leading slash if present to avoid double slashes
      const cleanPath = path.startsWith('/') ? path.slice(1) : path;
      return `${assetBaseUrl}/${cleanPath}`;
    },
    [assetBaseUrl]
  );

  // Memoized context value
  const contextValue = useMemo<SlotThemeContextValue>(
    () => ({
      theme,
      renderQuality,
      setRenderQuality,
      getSymbol,
      getAssetUrl,
      audioEnabled,
      setAudioEnabled,
    }),
    [
      theme,
      renderQuality,
      setRenderQuality,
      getSymbol,
      getAssetUrl,
      audioEnabled,
    ]
  );

  return (
    <SlotThemeContext.Provider value={contextValue}>
      {children}
    </SlotThemeContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useSlotTheme(): SlotThemeContextValue {
  const context = useContext(SlotThemeContext);

  if (!context) {
    throw new Error('useSlotTheme must be used within a SlotThemeProvider');
  }

  return context;
}

// =============================================================================
// Utility Hooks
// =============================================================================

/**
 * Get quality-specific settings for the current render quality
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useQualitySettings() {
  const { theme, renderQuality } = useSlotTheme();

  return useMemo(() => {
    const defaults = {
      shadows: false,
      particles: false,
      postProcessing: false,
      antialiasing: false,
      modelDetail: 'low' as const,
    };

    return theme.adaptive?.qualitySettings[renderQuality] ?? defaults;
  }, [theme.adaptive, renderQuality]);
}

/**
 * Get a specific symbol definition
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSymbol(symbolId: SymbolId): SymbolDefinition | undefined {
  const { getSymbol } = useSlotTheme();
  return useMemo(() => getSymbol(symbolId), [getSymbol, symbolId]);
}

/**
 * Get all symbols of a specific type
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSymbolsByType(type: SymbolDefinition['type']): SymbolDefinition[] {
  const { theme } = useSlotTheme();

  return useMemo(() => {
    return Object.values(theme.symbols).filter((symbol) => symbol.type === type);
  }, [theme.symbols, type]);
}

/**
 * Check if we should render 3D or fall back to 2D
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useShouldRender3D(): boolean {
  const { renderQuality } = useSlotTheme();
  return renderQuality !== '2d';
}

// =============================================================================
// Export Context for advanced usage
// =============================================================================

export { SlotThemeContext };
